import type { Ref } from 'vue'
import type { UseFetchOptions, NuxtError } from '#app'
import type { ApiResponse } from '~~/layers/base/types/apiResponseHandler'
import { mergeCapacitorDesignerFetchOptions } from '~~/layers/base/utils/mergeCapacitorDesignerFetchOptions'
import { applyClientIncompatibleFromFetchError } from '~~/layers/base/utils/designerMinAppVersionPolicy'
import { isFetchAbortedError, isLikelyClientTransportError } from '~~/layers/base/utils/fetchAbort'
import { isCapacitorShell } from '~~/layers/base/utils/isCapacitorShell'
import { isSessionExpiryCandidate } from '~~/layers/base/utils/isSessionExpiryCandidate'
// import { authInterruption } from '@/utils/authInterruption'

/**
 * Module-level singleton to track active API fetch requests.
 * Used to suppress intermediate retry errors in DataDog while allowing the final error to be reported.
 *
 * Using a module-level Set instead of attaching to the window object:
 * - Keeps state scoped to this module (better encapsulation)
 * - Avoids polluting the global window object
 * - Consistent with other patterns in the codebase (e.g., useDrawerHistory.ts, paperPreview.store.ts)
 * - Easier to test and maintain
 *
 * Exported for use by the DataDog plugin to check if a request URL is currently active.
 */
export const activeUseApiFetchRequests = new Set<string>()

/**
 * Custom fetch composable for consistent API requests, error handling, and automatic retries.
 *
 * Uses `$fetch` directly for maximum control and robust retry handling. Provides reactive state
 * management and consistent error transformation.
 *
 * Features:
 * - Automatic retry logic for transient errors (408, 413, 429, 500, 502, 503, 504)
 * - Immediate retry on failure (no delay between retry attempts)
 * - Consistent error transformation to NuxtError format
 * - Base URL handling for production environments
 * - Capacitor (`capacitor://localhost`): attaches `Authorization: Bearer` from the same store as
 *   Better Auth client (plain `$fetch` does not use authClient hooks, so this is required for
 *   cross-origin calls when `baseURL` is set)
 * - Reactive state management (data, error, pending)
 * - Refresh function to re-execute the request
 *
 * @param url - The URL to fetch (can be a string or function that returns a string).
 * @param options - Options for `$fetch` (e.g., method, body, headers).
 * @param maxRetries - Maximum number of retry attempts (default: 3).
 * @returns Object with reactive refs: { data, error, pending, refresh }.
 */
export const useApiFetch = async <T, E = unknown>(
    url: string | (() => string),
    options: UseFetchOptions<T> = {},
    maxRetries = 3
): Promise<ApiResponse<T, E>> => {
    const baseURL = useApiBaseUrl()

    // Capacitor-only: fold fetch outcomes into the network store. Resolved here (composable
    // setup) so we never instantiate the store from a plain util or on the web SPA.
    const networkStore = import.meta.client && isCapacitorShell() ? useNetworkStore() : null

    // Retry options for ofetch (used by $fetch under the hood)
    // These options control automatic retry behavior for transient server errors
    const retryOptions = {
        retry: maxRetries, // Maximum number of retry attempts
        retryDelay: 0, // Immediate retry (no delay between retry attempts)
        retryStatusCodes: [408, 413, 429, 500, 502, 503, 504] as number[], // Only retry on these HTTP status codes
    }

    /**
     * Transform error responses into NuxtError format for consistent error handling.
     * This extracts error messages from various response formats (JSON, string, etc.)
     * and standardizes them into the NuxtError structure.
     */
    const handleError = (response: {
        _data: unknown
        status: number
        statusText: string
        body: unknown
    }): NuxtError => {
        let errorData: unknown = response._data

        if (typeof errorData === 'string') {
            try {
                errorData = JSON.parse(errorData) as Record<string, unknown>
            } catch {
                errorData = { message: errorData }
            }
        }

        const errorMessage =
            (typeof errorData === 'object' &&
                errorData !== null &&
                'message' in errorData &&
                typeof errorData.message === 'string' &&
                errorData.message) ||
            response.statusText ||
            'Unknown API error'

        const apiError: NuxtError = {
            statusCode: response.status,
            statusMessage: response.statusText,
            message: errorMessage,
            data: response.body,
        }

        return apiError
    }

    // Resolve URL string (supports both string and function)
    const resolveUrl = (): string => {
        return typeof url === 'string' ? url : typeof url === 'function' ? url() : String(url)
    }

    // Create reactive state
    const data = ref<T | undefined>(undefined)
    const error = ref<E | null>(null)
    const pending = ref(true)

    // Fetch function that can be called directly or via refresh
    const executeFetch = async () => {
        pending.value = true
        error.value = null

        const requestUrl = resolveUrl()

        // Mark this request as active to suppress intermediate retry errors in DataDog
        // This prevents DataDog from reporting each retry attempt
        // Uses module-level singleton instead of window object for better encapsulation
        if (import.meta.client) {
            activeUseApiFetchRequests.add(requestUrl)
        }

        try {
            // Pass retry options directly to $fetch (ofetch handles retries internally)
            // Errors are transformed in the catch block after all retries are exhausted
            // The catch block only runs after all retries fail, so the global handler will only see the final error
            const fetchOptions = await mergeCapacitorDesignerFetchOptions({
                ...options,
                ...(baseURL && { baseURL }),
                ...retryOptions,
            })

            data.value = await $fetch<T>(requestUrl, fetchOptions)
            error.value = null
            networkStore?.notifyApiFetchReachable()
        } catch (err: unknown) {
            // Remove from active requests BEFORE handling error
            // This ensures the final error (exception) is not suppressed by DataDog's resource tracking
            // Resource errors from intermediate retries are suppressed, but the final exception
            // thrown here will be caught by the global error handler and reported to DataDog
            if (import.meta.client) {
                activeUseApiFetchRequests.delete(requestUrl)
            }

            // Transform error after all retries are exhausted
            // ofetch throws errors with response data when status codes indicate failure
            // We check if the error has response properties and transform it to NuxtError format
            if (
                err &&
                typeof err === 'object' &&
                'status' in err &&
                'statusText' in err &&
                '_data' in err
            ) {
                // HTTP response received — path to the API works (force-update / 4xx / 5xx still count).
                networkStore?.notifyApiFetchReachable()
                applyClientIncompatibleFromFetchError(err)
                error.value = handleError(
                    err as {
                        _data: unknown
                        status: number
                        statusText: string
                        body: unknown
                    }
                ) as E

                // Candidate only — `handleSessionExpired` confirms the session is really gone
                // before signing anyone out (our own routes 401 for several non-session reasons).
                if (
                    import.meta.client &&
                    isSessionExpiryCandidate((err as { status: number }).status, requestUrl)
                ) {
                    void handleSessionExpired()
                }
            } else if (isFetchAbortedError(err)) {
                // Intentional cancellation (e.g. start-page route leave); not a user-facing error.
                error.value = null
            } else if (isLikelyClientTransportError(err)) {
                // Normalize client transport failures (no HTTP response) into a stable shape so
                // call sites can branch on `code` without parsing platform-specific error strings.
                networkStore?.notifyApiFetchTransportFailure()
                error.value = {
                    code: 'CLIENT_TRANSPORT_ERROR',
                    message:
                        'Network request failed before reaching the server. Check your connection and try again.',
                    data: null,
                } as E
            } else {
                // Error doesn't have response structure, pass through as-is
                error.value = err as E
            }
        } finally {
            // Clean up: ensure request is removed from active set even on success
            // This prevents memory leaks if the request completes successfully
            if (import.meta.client) {
                activeUseApiFetchRequests.delete(requestUrl)
            }
            pending.value = false
        }
    }

    // Execute initial fetch
    await executeFetch()

    // Refresh function to re-execute the request
    const refresh = async () => {
        await executeFetch()
    }

    return {
        data: data as Ref<T | undefined>,
        error: error as Ref<E | null>,
        pending,
        refresh,
    }
}
